import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { usePostHog } from 'posthog-js/react';

import { closeModal } from 'loot-core/client/actions';
import { type AuthState } from 'loot-core/client/state-types/auth';
import { send } from 'loot-core/platform/client/fetch';

import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from '../../../hooks/useNavigate';
import { theme } from '../../../style';
import { ButtonWithLoading } from '../../common/Button';
import { Input } from '../../common/Input';
import { Link } from '../../common/Link';
import { Text } from '../../common/Text';
import { View } from '../../common/View';
import { FormField, FormLabel } from '../../forms';
import { useSessionFlag } from '../../../hooks/useSessionFlag';

type LoginFormProps = {
  initialEmail?: string;
  onLogin?: (authState: AuthState) => void;
  switchToSignup?: () => void;
  switchTypeToForgotPassword?: () => void;
};

export function LoginForm({
  initialEmail,
  onLogin,
  switchToSignup,
  switchTypeToForgotPassword,
}: LoginFormProps) {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { refreshAuthState } = useAuth();
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [isCredentialsInvalid, setIsCredentialsInvalid] = useState(false);
  const [postAuth] = useSessionFlag('postAuth');

  async function onSubmit(e) {
    setIsCredentialsInvalid(false);
    e.preventDefault();

    const loginData = {
      email,
      password,
    };

    posthog.capture('Remote login', {
      Email: loginData.email,
    });

    const result = await send(
      'subscribe-sign-in',
      {
        email,
        password,
      },
      { catchErrors: true },
    );

    if (result.error) {
      setIsCredentialsInvalid(true);
    } else {
      const result = await refreshAuthState();
      dispatch(closeModal());
      if (onLogin) {
        onLogin(result);
      } else {
        if (postAuth === 'ynab') {
          navigate('/onboarding');
        } else {
          navigate('/');
        }
      }
    }
  }

  const navigateToSignup = () => {
    if (switchToSignup) {
      switchToSignup();
    } else {
      navigate('/signup');
    }
  };

  const navigateToForgotPassword = () => {
    if (switchTypeToForgotPassword) {
      switchTypeToForgotPassword();
    } else {
      navigate('/forgot-password');
    }
  };

  return (
    <View style={{ overflow: 'auto', padding: 8 }}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 10,
        }}
        onSubmit={onSubmit}
      >
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel title="Your email address" htmlFor="Email" />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            type="email"
            name="email"
            value={email}
            required={true}
            onChange={e => {
              setEmail(e.target.value);
            }}
          />
        </FormField>
        <FormField style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            <FormLabel title="Password" htmlFor="password" />
            &nbsp;<Text style={{ color: theme.errorText }}> * </Text>
          </View>
          <Input
            type="password"
            name="password"
            required={true}
            onChange={e => {
              setPassword(e.target.value);
            }}
          />
        </FormField>

        {isCredentialsInvalid && (
          <View style={{ color: theme.errorText }}>
            Invalid email or password.
          </View>
        )}

        <ButtonWithLoading type="primary" style={{ margin: 2 }}>
          Login
        </ButtonWithLoading>
      </form>
      <View style={{ alignItems: 'center', marginTop: 16, gap: 8 }}>
        <Link
          variant="text"
          style={{ color: theme.pageTextLink }}
          onClick={navigateToForgotPassword}
        >
          Forgot password?
        </Link>
        <div
          style={{
            margin: '10px 0 0',
            textAlign: 'center',
          }}
        >
          Looking to&nbsp;
          <span>
            <Link
              variant="text"
              style={{ color: theme.pageTextLink, whiteSpace: 'nowrap' }}
              onClick={navigateToSignup}
            >
              Signup
            </Link>
          </span>
          ?
        </div>
      </View>
    </View>
  );
}
